import { useState } from "react";
import { Link } from "react-router-dom";

export default function FAQ() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const categories = [
    {
      name: "General",
      faqs: [
        {
          question: "What is ExamLock?",
          answer: "ExamLock is an AI-powered assessment platform that helps teachers create quizzes, assign secure tests, monitor student activity, and review timestamped activity logs."
        },
        {
          question: "Who is ExamLock for?",
          answer: "ExamLock is designed for teachers, departments, and schools that want faster assessment creation and clearer online exam monitoring."
        }
      ]
    },
    {
      name: "Secure Testing",
      faqs: [
        {
          question: "Does ExamLock stop all cheating?",
          answer: "No online tool can guarantee that cheating is impossible. ExamLock helps reduce opportunities for cheating by using fullscreen mode, tab-switch detection, copy/paste blocking, and timestamped activity logs."
        },
        {
          question: "What does ExamLock monitor?",
          answer: "ExamLock can log events such as fullscreen exits, tab switching, focus loss, and copy/paste attempts during secure assessments, depending on the settings enabled."
        },
        {
          question: "Does ExamLock use facial recognition or eye tracking?",
          answer: "No. ExamLock does not currently use facial recognition or eye tracking. The current secure mode focuses on browser-based activity monitoring."
        }
      ]
    },
    {
      name: "Students and Privacy",
      faqs: [
        {
          question: "Do students need to install anything?",
          answer: "No. Students open ExamLock in their browser. No app or browser extension is required for the current secure mode."
        },
        {
          question: "Do students need accounts?",
          answer: "For secure assessments, student accounts are recommended so teachers can track real names, progress, submissions, and activity logs."
        },
        {
          question: "Is student data sold?",
          answer: "No. Student data is not sold."
        },
        {
          question: "How is student data protected?",
          answer: "Student data is encrypted in transit and at rest using Google Cloud infrastructure. Designed with privacy-conscious practices. DPA available on request."
        }
      ]
    },
    {
      name: "Features",
      faqs: [
        {
          question: "Can I generate questions from lesson notes?",
          answer: "Yes. Teachers can paste lesson notes or learning objectives and use AI to generate questions."
        },
        {
          question: "Can I upload PDFs?",
          answer: "PDF upload is planned, but not part of the first simple version. For now, teachers can paste notes directly."
        },
        {
          question: "Is Practice Mode available?",
          answer: "Practice Mode is planned. The current focus is secure assessment creation, assignment, and monitoring."
        },
        {
          question: "What question types are supported?",
          answer: "Currently: Multiple choice, True/False, Fill in the blank, Short answer, and Essay questions. Teachers review all AI-generated questions before assigning."
        }
      ]
    },
    {
      name: "Pricing",
      faqs: [
        {
          question: "Is there a free plan?",
          answer: "Yes. The Free Beta plan is available for teachers who want to try ExamLock with a small class or pilot group."
        },
        {
          question: "Why is AI generation limited?",
          answer: "AI generation has real usage costs. Limits help keep ExamLock sustainable and affordable."
        },
        {
          question: "Do I need a credit card for the Free Beta?",
          answer: "No credit card is required to start using the Free Beta plan."
        },
        {
          question: "Can schools pay by invoice?",
          answer: "Yes. School Plan customers can arrange invoice-based billing. Contact us for details."
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-sky-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Find answers to common questions about ExamLock
          </p>
        </div>
      </div>

      {/* FAQ Categories */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {categories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">{category.name}</h2>
                <div className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => {
                    const key = `${categoryIndex}-${faqIndex}`;
                    const isOpen = openFAQ === key;
                    return (
                      <div key={faqIndex} className="bg-white border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                          className="w-full text-left p-6 flex justify-between items-center hover:bg-gray-50 transition-colors rounded-lg"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                          <span className="text-2xl text-gray-400 flex-shrink-0">{isOpen ? "−" : "+"}</span>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Still have questions?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact ExamLock and we'll get back to you during Doha business hours.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-lg shadow-lg transition-all"
          >
            Contact ExamLock
          </Link>
        </div>
      </div>

    </div>
  );
}
